import {IMessageCode, isBaseMessageCode, MessageCode} from "@mindpath/shared";
import {ensureMessageCodeIsThrowable, MapError} from "./error-mapper";
import {HttpErrorResponse, HttpInterceptorFn} from "@angular/common/http";
import {catchError} from "rxjs";

export type HandledResponse<TSuccess> = { success: TSuccess, error: null } | { success: null, error: IMessageCode };

export async function HandleResponse<TSuccess>(resp: Response | Promise<Response>): Promise<HandledResponse<TSuccess>> {
  try {
    if (resp instanceof Promise) {
      resp = await resp;
    }
    if (resp.ok) {
      return {success: await resp.json() as TSuccess, error: null};
    }
    return {success: null!, error: MapError(await resp.text())};
  } catch (e) {
    return {success: null!, error: MapError(e)};
  }
}

export const httpErrorMappingInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(catchError(HandleNetworkErrors));
}

export async function HandleNetworkErrors(error: unknown): Promise<never> {
  if (!(error instanceof HttpErrorResponse)) {
    // Thrown before any response existed, such as an interceptor refusing to send the request without a
    // token. It is already one of our errors; reading `error.error` off it would turn it into BSR0000.
    throw ensureMessageCodeIsThrowable(MapError(error, {noLog: true}));
  }

  console.error('Network error', error);
  let errorToMap: unknown = error.error;

  if (error.error instanceof Response) {
    console.log('error.error is a Response', error.error);
    try {
      if (error.error.headers.get('content-type')?.includes('application/json')) {
        errorToMap = await error.error.json();
      } else if (error.status === 403 || error.status === 401) {
        errorToMap = MessageCode.ATH0005;
      } else if (error.status === 0) {
        errorToMap = MessageCode.Browser_Request_Dropped;
      }
    } catch (e) {
      console.error('Error parsing error.error', e);
      errorToMap = MapError(e, {
        noLog: true,
      });
    }
  } else if (!isBaseMessageCode(error.error)) {
    if (error.status === 403 || error.status === 401) {
      errorToMap = MessageCode.ATH0005;
    } else if (error.message === 'Load failed' || error.message === 'Failed to fetch' || error.status === 0) {
      errorToMap = MessageCode.Browser_Request_Dropped;
    }
  }

  throw ensureMessageCodeIsThrowable(MapError(errorToMap, {
    noLog: true
  })).with({
    cause: error,
    useCauseStack: true,
  });
}
